import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { applyPageMetadata } from "../../lib/seo";
import { Brand } from "../../components/Brand";
import "./games-hub.css";

interface GameEntry {
  id: string;
  slug: string;
  title: string;
  subtitle: string;
  category: "all" | "puzzle" | "action" | "survival" | "fighting";
  categoryLabel: string;
  badge: string;
  badgeColor?: string;
  icon: string;
  thumbnailBg: string;
  rating: number;
  players: string;
  engine: string;
  description: string;
  highlights: string[];
  isFeatured?: boolean;
}

const GAMES_DATA: GameEntry[] = [
  {
    id: "happy-tomato",
    slug: "/games/happy-tomato",
    title: "Happy Tomato: Water Flow",
    subtitle: "Teka-Teki Susun Pipa Air 20 Stage",
    category: "puzzle",
    categoryLabel: "Puzzle & Otak",
    badge: "Baru • 20 Stages",
    badgeColor: "#ea580c",
    icon: "🍅",
    thumbnailBg: "radial-gradient(ellipse at 50% 40%, #1e4620 0%, #0f2715 60%, #07150a 100%)",
    rating: 4.9,
    players: "1 Pemain",
    engine: "Cocos Creator 3.8.8 WebGL",
    description:
      "Bantu tanaman tomat yang sedang kehausan dengan memutar dan merangkai jalur pipa air yang benar. Dilengkapi 20 stage progresif (3x3 hingga 7x7), pipa pengecoh yang menantang logika, serta fitur Kunci Jawaban otomatis interaktif.",
    highlights: [
      "20 Stage Bertingkat (Easy, Medium, Hard)",
      "Pipa Pengecoh & Multi-Aliran Air",
      "Kunci Jawaban Auto-Solve Interaktif",
      "Audio Web Prosedural & Efek Partikel Air",
    ],
    isFeatured: true,
  },
  {
    id: "tembak-monyetnya",
    slug: "/games/tembak-monyetnya",
    title: "Tembak Monyetnya",
    subtitle: "Ketapel Aksi Satu Jempol di Rimba & Kawah",
    category: "action",
    categoryLabel: "Aksi & Arcade",
    badge: "Populer",
    badgeColor: "#16a34a",
    icon: "🐒",
    thumbnailBg: "radial-gradient(ellipse at 50% 40%, #1e3a2f 0%, #10211a 60%, #08110d 100%)",
    rating: 4.8,
    players: "1 Pemain (One-Thumb)",
    engine: "HTML5 Canvas 2D",
    description:
      "Game ketangkasan ketapel satu jempol yang seru dan adiktif! Tarik dan lepaskan kantung ketapel untuk mengusir monyet nakal pencuri buah di tengah hutan rimbun dan lereng kawah gunung berapi.",
    highlights: [
      "Kontrol Ketapel Satu Jempol Responsif",
      "Stage Hutan Tropis & Lereng Gunung Berapi",
      "Animasi Taunt Monyet 4K & Reaksi Lucu",
      "Sistem Combo & Skor Ketepatan Tembak",
    ],
  },
  {
    id: "tantang-batasanmu",
    slug: "/games/tantang-batasanmu",
    title: "Tantang Batasanmu",
    subtitle: "Pixel Survival Hack & Slash RPG",
    category: "survival",
    categoryLabel: "Survival & RPG",
    badge: "Action RPG",
    badgeColor: "#9333ea",
    icon: "⚔️",
    thumbnailBg: "radial-gradient(ellipse at 50% 40%, #2e1a47 0%, #170d25 60%, #0a0512 100%)",
    rating: 4.9,
    players: "1 Pemain",
    engine: "Canvas 2D Pixel Art",
    description:
      "Bertahanlah dari serbuan monster tanpa henti di dunia pixel retro! Pilih karakter Swordmaster atau Blade Dancer, kumpulkan kristal jiwa musuh, tingkatkan atribut, dan gabungkan skill pedang pamungkasmu.",
    highlights: [
      "Karakter Unik: Swordmaster & Blade Dancer",
      "Sistem Upgrade & Evolusi Skill Magis",
      "Serbuan Musuh Masif Bergelombang",
      "Pixel Art Retro Otentik & BGM Atmosferik",
    ],
  },
  {
    id: "tantangan-terbuka",
    slug: "/games/tantangan-terbuka",
    title: "Tantangan Terbuka",
    subtitle: "Duel Pendekar Bela Diri 1v1",
    category: "fighting",
    categoryLabel: "Fighting 1v1",
    badge: "PvE & 2P PvP",
    badgeColor: "#dc2626",
    icon: "🥊",
    thumbnailBg: "radial-gradient(ellipse at 50% 40%, #3b1818 0%, #1f0b0b 60%, #100505 100%)",
    rating: 4.7,
    players: "1 - 2 Pemain (PvE / Local PvP)",
    engine: "Canvas 2D Fighter",
    description:
      "Pertarungan seni bela diri legendaris antara pendekar Ren dan Boma! Kuasai jurus pukulan kilat, tendangan pelontar, serangan udara, dan pertahanan rapat di arena Padepokan dan Kuil Rembulan.",
    highlights: [
      "Mode PvE Lawan AI Cerdas & Local 2-Player",
      "Dua Pilihan Arena: Padepokan & Kuil Rembulan",
      "Sistem Kombo, Launcher & Knockback",
      "Sound FX Pukulan & Tangkisan Reaktif",
    ],
  },
  {
    id: "bertahan-dari-anomali",
    slug: "/games/bertahan-dari-anomali",
    title: "Bertahan Dari Anomali",
    subtitle: "Top-Down Tactical Survival RPG",
    category: "survival",
    categoryLabel: "Survival & RPG",
    badge: "Sci-Fi Survival",
    badgeColor: "#0284c7",
    icon: "☢️",
    thumbnailBg: "radial-gradient(ellipse at 50% 40%, #132f38 0%, #0a1b21 60%, #040d10 100%)",
    rating: 4.9,
    players: "1 Pemain",
    engine: "Canvas 2D + Mini-Map Radar",
    description:
      "Terjebak di dalam zona anomali misterius yang mematikan. Pantau pergerakan ancaman melalui radar mini-map real-time, manfaatkan medan sungai dan pepohonan, serta bertahan dari gelombang makhluk anomali.",
    highlights: [
      "Radar Mini-Map Real-time 360 Derajat",
      "Variasi Musuh: Anomali Cepat & Mutan Raksasa",
      "Taktik Navigasi Sungai & Posisi Tembak",
      "Sistem Audio Deteksi Lingkungan",
    ],
  },
];

type CategoryFilter = "all" | "puzzle" | "action" | "survival" | "fighting";

export function GamesHubPage() {
  const [selectedCategory, setSelectedCategory] = useState<CategoryFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    applyPageMetadata({
      title: "Games | Steria House Arcade",
      description:
        "Koleksi game web interaktif Steria House: Happy Tomato, Tembak Monyetnya, Tantang Batasanmu, Tantangan Terbuka, dan Bertahan Dari Anomali. Mainkan gratis langsung di browser!",
      canonicalPath: "/games",
    });
  }, []);

  const filteredGames = useMemo(() => {
    return GAMES_DATA.filter((game) => {
      const matchCategory =
        selectedCategory === "all" || game.category === selectedCategory;
      const matchSearch =
        searchQuery.trim() === "" ||
        game.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        game.subtitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        game.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        game.highlights.some((h) => h.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchCategory && matchSearch;
    });
  }, [selectedCategory, searchQuery]);

  const featuredGame = GAMES_DATA.find((g) => g.isFeatured) || GAMES_DATA[0];

  return (
    <div className="gh-page">
      {/* Top Navbar */}
      <header className="gh-header">
        <div className="gh-header-inner">
          <Brand />
          <nav className="gh-header-nav" aria-label="Navigasi Games">
            <Link to="/" className="gh-nav-link">
              Beranda
            </Link>
            <Link to="/games" className="gh-nav-link active">
              Games Hub
            </Link>
            <a
              href="mailto:steriahouse@gmail.com"
              className="gh-cta-btn"
            >
              Hubungi Kami ↗
            </a>
          </nav>
        </div>
      </header>

      <main className="gh-main">
        {/* Hero Section */}
        <section className="gh-hero">
          <div className="gh-hero-pill">✦ STERIA HOUSE ARCADE</div>
          <h1 className="gh-hero-title">
            Koleksi Game Web Interaktif <span>Steria House</span>
          </h1>
          <p className="gh-hero-desc">
            Game arcade, teka-teki logika, survival RPG, dan petarung 2D berkualitas tinggi.
            Didesain untuk dimainkan instan langsung di browsermu — tanpa unduhan, tanpa instalasi, dan 100% responsif di desktop maupun smartphone.
          </p>

          {/* Quick Metrics Bar */}
          <div className="gh-stats-bar">
            <div className="gh-stat-item">
              <span className="gh-stat-val">5 Game</span>
              <span className="gh-stat-lbl">Siap Dimainkan</span>
            </div>
            <div className="gh-stat-divider" />
            <div className="gh-stat-item">
              <span className="gh-stat-val">100% Web</span>
              <span className="gh-stat-lbl">WebGL & Canvas 2D</span>
            </div>
            <div className="gh-stat-divider" />
            <div className="gh-stat-item">
              <span className="gh-stat-val">60 FPS</span>
              <span className="gh-stat-lbl">Performa Ringan</span>
            </div>
            <div className="gh-stat-divider" />
            <div className="gh-stat-item">
              <span className="gh-stat-val">0 B</span>
              <span className="gh-stat-lbl">Audio Prosedural</span>
            </div>
          </div>
        </section>

        {/* Featured Game Spotlight */}
        {featuredGame && (
          <section className="gh-featured-section" aria-label="Game Pilihan Utama">
            <div className="gh-featured-card">
              <div className="gh-featured-visual">
                <div className="gh-featured-badge">⭐ PILIHAN UTAMA</div>
                <div className="gh-featured-icon-wrap">
                  <span className="gh-featured-big-icon">{featuredGame.icon}</span>
                </div>
                <div className="gh-featured-tech-pill">{featuredGame.engine}</div>
              </div>

              <div className="gh-featured-body">
                <div className="gh-featured-meta">
                  <span className="gh-tag">{featuredGame.categoryLabel}</span>
                  <span className="gh-rating">★ {featuredGame.rating} (Rating Tertinggi)</span>
                  <span className="gh-players">👤 {featuredGame.players}</span>
                </div>

                <h2 className="gh-featured-title">{featuredGame.title}</h2>
                <p className="gh-featured-subtitle">{featuredGame.subtitle}</p>
                <p className="gh-featured-desc">{featuredGame.description}</p>

                <div className="gh-highlights-grid">
                  {featuredGame.highlights.map((hl, i) => (
                    <div key={i} className="gh-highlight-tag">
                      ✓ {hl}
                    </div>
                  ))}
                </div>

                <div className="gh-featured-actions">
                  <Link to={featuredGame.slug} className="gh-btn-play-primary">
                    ▶ Mainkan Sekarang
                  </Link>
                  <a
                    href="/games/happy-tomato/game.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gh-btn-secondary"
                  >
                    Buka di Tab Baru ↗
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Filter and Search Bar */}
        <section className="gh-controls-section">
          <div className="gh-category-tabs" role="tablist">
            <button
              type="button"
              className={`gh-tab ${selectedCategory === "all" ? "active" : ""}`}
              onClick={() => setSelectedCategory("all")}
            >
              Semua ({GAMES_DATA.length})
            </button>
            <button
              type="button"
              className={`gh-tab ${selectedCategory === "puzzle" ? "active" : ""}`}
              onClick={() => setSelectedCategory("puzzle")}
            >
              🧩 Puzzle (1)
            </button>
            <button
              type="button"
              className={`gh-tab ${selectedCategory === "action" ? "active" : ""}`}
              onClick={() => setSelectedCategory("action")}
            >
              🎯 Aksi / Arcade (1)
            </button>
            <button
              type="button"
              className={`gh-tab ${selectedCategory === "survival" ? "active" : ""}`}
              onClick={() => setSelectedCategory("survival")}
            >
              🛡️ Survival & RPG (2)
            </button>
            <button
              type="button"
              className={`gh-tab ${selectedCategory === "fighting" ? "active" : ""}`}
              onClick={() => setSelectedCategory("fighting")}
            >
              🥊 Fighting 1v1 (1)
            </button>
          </div>

          <div className="gh-search-wrap">
            <input
              type="text"
              placeholder="Cari judul, genre, atau mekanik game..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="gh-search-input"
              aria-label="Cari Game"
            />
            {searchQuery && (
              <button
                type="button"
                className="gh-search-clear"
                onClick={() => setSearchQuery("")}
                title="Hapus pencarian"
              >
                ✕
              </button>
            )}
          </div>
        </section>

        {/* Games Directory Grid */}
        <section className="gh-games-grid-section" aria-label="Daftar Semua Game">
          {filteredGames.length === 0 ? (
            <div className="gh-empty-state">
              <span className="gh-empty-icon">🔍</span>
              <h3>Game tidak ditemukan</h3>
              <p>Tidak ada game yang sesuai dengan kata kunci &ldquo;{searchQuery}&rdquo;.</p>
              <button
                type="button"
                className="gh-btn-secondary"
                onClick={() => {
                  setSelectedCategory("all");
                  setSearchQuery("");
                }}
              >
                Reset Filter
              </button>
            </div>
          ) : (
            <div className="gh-games-grid">
              {filteredGames.map((game) => (
                <article key={game.id} className="gh-game-card">
                  <div
                    className="gh-card-header"
                    style={{ background: game.thumbnailBg }}
                  >
                    <span
                      className="gh-card-badge"
                      style={{ backgroundColor: game.badgeColor || "#059669" }}
                    >
                      {game.badge}
                    </span>
                    <span className="gh-card-engine">{game.engine}</span>
                    <div className="gh-card-icon-center">
                      <span className="gh-card-big-emoji">{game.icon}</span>
                    </div>
                  </div>

                  <div className="gh-card-body">
                    <div className="gh-card-category-strip">
                      <span className="gh-tag">{game.categoryLabel}</span>
                      <span className="gh-card-rating">★ {game.rating}</span>
                    </div>

                    <h3 className="gh-card-title">{game.title}</h3>
                    <p className="gh-card-subtitle">{game.subtitle}</p>
                    <p className="gh-card-desc">{game.description}</p>

                    <div className="gh-card-features">
                      {game.highlights.slice(0, 3).map((hl, i) => (
                        <div key={i} className="gh-card-feature-item">
                          <span className="gh-check-dot">●</span> {hl}
                        </div>
                      ))}
                    </div>

                    <div className="gh-card-footer">
                      <span className="gh-card-player-count">👥 {game.players}</span>
                      <Link to={game.slug} className="gh-card-play-btn">
                        Mainkan Game →
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Studio Philosophy / Quality Guarantee */}
        <section className="gh-guarantee-section">
          <div className="gh-guarantee-card">
            <h2>✦ Komitmen Steria House Games</h2>
            <div className="gh-guarantee-grid">
              <div className="gh-guarantee-item">
                <span className="gh-g-icon">⚡</span>
                <h4>Tanpa Unduhan (Instant Play)</h4>
                <p>Cukup klik dan mainkan langsung di browser ponsel atau laptop dalam hitungan detik.</p>
              </div>
              <div className="gh-guarantee-item">
                <span className="gh-g-icon">🛡️</span>
                <h4>100% Legal & Commercial-Safe</h4>
                <p>Aset visual, musik, dan efek suara berlisensi legal CC0 atau karya orisinal studio.</p>
              </div>
              <div className="gh-guarantee-item">
                <span className="gh-g-icon">🎵</span>
                <h4>Audio Sintetis Prosedural</h4>
                <p>Menggunakan Web Audio API sintetis berukuran 0-byte yang jernih, ringan, dan dinamis.</p>
              </div>
              <div className="gh-guarantee-item">
                <span className="gh-g-icon">📱</span>
                <h4>Desain Responsif Adaptif</h4>
                <p>Dioptimalkan sempurna untuk layar sentuh ponsel potret 9:16 maupun monitor desktop.</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="gh-footer">
        <div className="gh-footer-inner">
          <div className="gh-footer-brand">
            <Brand />
            <p>
              Steria House adalah studio produk digital yang merancang software, pengalaman AI,
              dan game interaktif yang memperkaya kehidupan manusia.
            </p>
          </div>
          <div className="gh-footer-links">
            <h4>Eksplorasi</h4>
            <Link to="/">Beranda</Link>
            <Link to="/games">Daftar Game</Link>
            <Link to="/games/happy-tomato">Happy Tomato</Link>
            <Link to="/games/tembak-monyetnya">Tembak Monyetnya</Link>
          </div>
          <div className="gh-footer-links">
            <h4>Kontak Studio</h4>
            <a href="mailto:steriahouse@gmail.com">steriahouse@gmail.com</a>
            <a href="https://wa.me/6287778037633" target="_blank" rel="noopener noreferrer">
              WhatsApp (+6287778037633)
            </a>
          </div>
        </div>
        <div className="gh-footer-bottom">
          <span>© {new Date().getFullYear()} Steria House. Seluruh hak cipta dilindungi undang-undang.</span>
          <span>Dirancang untuk hiburan bermakna & performa tinggi.</span>
        </div>
      </footer>
    </div>
  );
}
